import React, { useCallback, useMemo, useState } from 'react';
import _ from 'lodash';

import PERMISSIONS from '@/common/permissions';
import { WarnModal } from '@/components/elements';
import { useHasAccess } from '@/modules/core/hooks';
import { useGetRenewals } from '@/modules/Renewals/hooks';
import { useAllowedRenewals } from '@/modules/Renewals/hooks';
import {
  RenewalCanceledBanner,
  RenewalNewBanner,
  RenewalRequestedBanner,
} from '@/modules/Renewals/RenewalBanners';
import { RenewalStatus } from '@/modules/Renewals/renewals.types';
import { Renewal } from '@/modules/Renewals/renewals.types.ts';

import { useRenewalModals } from './useRenewalModals';

interface SlideItem {
  node: React.ReactNode;
  id: number;
  modalWindow?: React.ReactNode;
}

export const useBannersList = () => {
  const [isRenewDetailsModalVisible, setIsRenewDetailsModalVisible] = useState(false);
  const [renewalData, setRenewalData] = useState<Renewal>();

  const isHasPermission = useHasAccess([{ permission: PERMISSIONS.REPORTS }]);
  const { renewals } = useGetRenewals();
  const allowedRenewals = useAllowedRenewals(renewals);

  const {
    errorModal,
    setErrorModal,
    errorPlanYearId,
    setErrorPlanYearId,
    visibleModals,
    openModal,
    closeModal,
    handleCompleteRenewal,
  } = useRenewalModals();

  const handleSetVisibleRenewalPopup = useCallback((value: boolean, renewData: Renewal) => {
    setIsRenewDetailsModalVisible(value);
    setRenewalData(renewData);
  }, []);
  const handleSetConfirmRenewalPopup = useCallback(
    (value: string) => {
      openModal(value);
    },
    [openModal]
  );

  const getRenewalModalWindow = useCallback(
    (planYearId: string) => (
      <WarnModal
        visible
        isPendingIcon
        onSetVisible={() => closeModal(planYearId)}
        header="Mark as Complete?"
        helptext="Once you confirm the renewal is complete, this banner will no longer be displayed."
        buttonText="Cancel"
        submitButtonText="Yes, Complete"
        onSubmit={() => handleCompleteRenewal(planYearId)}
      />
    ),
    [closeModal, handleCompleteRenewal]
  );

  const renewalBanners = useMemo<SlideItem[]>(() => {
    if (!isHasPermission || !allowedRenewals?.length) return [];
    return _.compact(
      allowedRenewals?.map((renewal) => {
        const planYearId = renewal?.planYearId?.toString();
        switch (renewal?.status) {
          case RenewalStatus.NEW:
            return {
              id: 2,
              node: (
                <RenewalNewBanner
                  endDate={renewal?.planYear?.validTo}
                  planYearId={planYearId}
                  onSetConfirmRenewModalVisible={() => handleSetConfirmRenewalPopup(planYearId)}
                />
              ),
              modalWindow: visibleModals[planYearId] ? getRenewalModalWindow(planYearId) : null,
            };
          case RenewalStatus.CANCELED:
            return {
              id: 3,
              node: (
                <RenewalCanceledBanner
                  onSetConfirmRenewModalVisible={() => handleSetConfirmRenewalPopup(planYearId)}
                  onSetRenewDetailModalVisible={() => handleSetVisibleRenewalPopup(true, renewal)}
                />
              ),
              modalWindow: visibleModals[planYearId] ? getRenewalModalWindow(planYearId) : null,
            };
          case RenewalStatus.REQUESTED:
            return {
              id: 4,
              node: (
                <RenewalRequestedBanner
                  onSetCofirmRenewModalVisible={() => handleSetConfirmRenewalPopup(planYearId)}
                  onSetRenewDetailModalVisible={() => handleSetVisibleRenewalPopup(true, renewal)}
                />
              ),
              modalWindow: visibleModals[planYearId] ? getRenewalModalWindow(planYearId) : null,
            };
          default:
            return null;
        }
      })
    );
  }, [
    isHasPermission,
    allowedRenewals,
    handleSetVisibleRenewalPopup,
    visibleModals,
    getRenewalModalWindow,
    handleSetConfirmRenewalPopup,
  ]);

  const banners = useMemo(() => {
    return _.compact([...renewalBanners]);
  }, [renewalBanners]);

  return {
    banners,
    renewalData,
    errorModal,
    isRenewDetailsModalVisible,
    setErrorModal,
    errorPlanYearId,
    setErrorPlanYearId,
    handleCompleteRenewal,
    handleSetVisibleRenewalPopup: setIsRenewDetailsModalVisible,
  };
};

export default useBannersList;
